import { FC, useCallback, useEffect } from 'react';
import { defineMessages, MessageDescriptor, useIntl } from 'react-intl';
import { LinkButton, Modal } from 'src/components';
import { ROUTES } from 'src/constants/routes.const';
import { FeatureTokenType } from 'src/api/monetisation/types';
import { useUserContext } from 'src/context/user';
import { TrackingApi } from 'src/api';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import memriseProSrc from './images/memrise-pro.png';
import * as S from './styles';

const msgs = defineMessages({
  description: { id: 'monetisation_Upsell_text_LimitReached' },
  cta: { id: 'immerse_businessmodel_upsell_upgrade_cta' },
});

const titleMsgs: Record<FeatureTokenType, MessageDescriptor> = defineMessages({
  videos: { id: 'monetisation_VideosUpsell_title' },
  conversations: { id: 'monetisation_ConversationsUpsell_title' },
  review_classic: { id: 'monetisation_ClassicReviewUpsell_title' },
  review_classic_blob_experience: {
    id: 'monetisation_ClassicReviewBlobUpsell_title',
    defaultMessage: "Don't let words slip away",
  },
  review_speed: { id: 'monetisation_SpeedReviewUpsell_title' },
  review_difficult_words: { id: 'monetisation_DifficultWordsUpsell_title' },
  pronunciation_from_my_words: { id: 'monetisation_PronunciationUpsell_title' },
  presentation_screen_pronunciation: { id: 'monetisation_PronunciationUpsell_title' },
  buddy_type_pronunciation: { id: 'monetisation_PronunciationUpsell_title' },
  buddy_type_sentence_builder: { id: 'monetisation_SentenceBuilderUpsell_title' },
  buddy_type_grammar: { id: 'monetisation_GrammarUpsell_title' },
  buddy_type_roleplay: { id: 'monetisation_RolePlayUpsell_title' },
  buddy_type_translator: { id: 'monetisation_TranslatorUpsell_title' },
  buddy_type_culture: { id: 'monetisation_CultureBuddyUpsell_title' },
  buddy_type_conjugation: { id: 'monetisation_ConjugationUpsell_title' },
  wordlist_bulk_create_learnables_with_ai: {
    id: 'monetisation_WordlistBulkCreateLearnablesWithAiUpsell_title',
  },
});

export const FeatureUpsellModal: FC<{
  tokenType: FeatureTokenType;
  buddyId?: string;
  onClose: () => void;
}> = ({ tokenType, buddyId, onClose }) => {
  const intl = useIntl();
  const { user } = useUserContext();
  const languagePairId = getLanguagePairId();
  const titleMsg = titleMsgs[tokenType];

  // Viewed modal event
  useEffect(() => {
    void TrackingApi.trackUserAction(
      'screen_viewed',
      'upsell_screen',
      languagePairId,
      !!user?.isPro,
      {
        feature: tokenType,
        ...(buddyId && { buddy_id: buddyId }),
      },
    );
    // We want this to fire only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCtaClick = useCallback(() => {
    void TrackingApi.trackUserAction(
      'clicked_upsell_upgrade_button',
      'upsell_screen',
      languagePairId,
      !!user?.isPro,
      { feature: tokenType },
    );
  }, [tokenType, user, languagePairId]);

  const handleDismiss = useCallback(() => {
    void TrackingApi.trackUserAction(
      'paywall_dismissed',
      'upsell_screen',
      languagePairId,
      !!user?.isPro,
      { feature: tokenType },
    );
    onClose();
  }, [tokenType, user, languagePairId, onClose]);

  return (
    <Modal isOpen darkCloseIcon handleClose={handleDismiss}>
      <S.ContentContainer data-testid="featureUpsellModal">
        <S.Image src={memriseProSrc} alt="" />
        {titleMsg && <S.Title>{intl.formatMessage(titleMsg)}</S.Title>}
        <S.Description>{intl.formatMessage(msgs.description)}</S.Description>
        <LinkButton
          buttonType="primary"
          buttonSize="large"
          wide
          href={{ pathname: ROUTES.payment.plans, query: { upsellTokenType: tokenType } }}
          onClick={onCtaClick}
        >
          <S.LockIcon name="padlock" />
          {intl.formatMessage(msgs.cta)}
        </LinkButton>
      </S.ContentContainer>
    </Modal>
  );
};
